import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSave, faPaperclip, faSpinner } from "@fortawesome/free-solid-svg-icons";

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: ${props => props.isOpen ? fadeIn : 'none'} 0.2s ease-out;
`;

const EditContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  overflow-y: auto;
  z-index: 10000;
  animation: ${props => props.isOpen ? slideUp : 'none'} 0.3s ease-out;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const EditHeader = styled.div`
  position: sticky;
  top: 0;
  background: #ffffff;
  border-bottom: 1px solid #e9ecef;
  padding: 20px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 10;
`;

const EditTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:before {
    content: '✏️';
    font-size: 18px;
  }
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const HeaderButton = styled.button`
  background: ${props => props.primary ? '#007bff' : '#ffffff'};
  border: ${props => props.primary ? 'none' : '1px solid #dee2e6'};
  color: ${props => props.primary ? 'white' : '#495057'};
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? '#0056b3' : '#f8f9fa'};
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    .spinner {
      animation: ${spin} 1s linear infinite;
    }
  }
`;

const EditContent = styled.div`
  padding: 24px 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const FieldGroup = styled.div`
  margin-bottom: 28px;
  position: relative;
  
  &:last-child {
    margin-bottom: 80px; /* 하단 여백 */
  }
`;

const FieldLabel = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 18px;
    background: #007bff;
    border-radius: 2px;
  }
  
  .required {
    color: #e74c3c;
    font-weight: 800;
    margin-left: 4px;
  }
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 16px 18px;
  border: 2px solid #e3f2fd;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  background: #ffffff;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    background: white;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
  
  &:disabled {
    background: #f8f9fa;
    color: #6c757d;
    border-color: #dee2e6;
    box-shadow: none;
  }
  
  &::placeholder {
    color: #adb5bd;
    font-weight: 400;
  }
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  padding: 16px 18px;
  border: 2px solid #e3f2fd;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  background: #ffffff;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  line-height: 1.6;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    background: white;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
  
  &:disabled {
    background: #f8f9fa;
    color: #6c757d;
    border-color: #dee2e6;
    box-shadow: none;
  }
  
  &::placeholder {
    color: #adb5bd;
    font-weight: 400;
  }
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 16px 18px;
  border: 2px solid #e3f2fd;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    background: white;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
  
  &:disabled {
    background: #f8f9fa;
    color: #6c757d;
    border-color: #dee2e6;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  padding: 16px 18px;
  background: #ffffff;
  border: 2px solid #e3f2fd;
  border-radius: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #bbdefb;
  }
  
  input[type="checkbox"] {
    width: 22px;
    height: 22px;
    cursor: pointer;
    accent-color: #007bff;
  }
`;

const SaveMessage = styled.div`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: #28a745;
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  z-index: 10001;
  opacity: ${props => props.show ? 1 : 0};
  transition: all 0.3s ease;
  transform: ${props => props.show 
    ? 'translateX(-50%) translateY(0)' 
    : 'translateX(-50%) translateY(20px)'
  };
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:before {
    content: '✅';
    font-size: 16px;
  }
`;

const MobileEditForm = ({ 
  isOpen, 
  onClose, 
  board = {}, 
  columns = [],
  codeDisplayMap = {},
  onSave
}) => {
  const [editData, setEditData] = useState({});
  const [isLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (isOpen && board) {
      setEditData({ ...board });
    }
  }, [isOpen, board]);

  // 팝업 열릴 때 배경 스크롤 비활성화
  useEffect(() => {
    if (isOpen) {
      // 현재 스크롤 위치 저장하고 body 스크롤 막기
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      // 스크롤 위치 복원
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }

    return () => {
      // 클린업 - 컴포넌트 언마운트시 스크롤 복원
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleFieldChange = (columnId, value) => {
    setEditData(prev => ({
      ...prev,
      [columnId]: value
    }));
  };

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      if (onSave) {
        await onSave(editData);
        setSaveMessage('저장되었습니다');
        setTimeout(() => setSaveMessage(''), 3000);
        setTimeout(() => onClose(), 1000);
      }
    } catch (error) {
      console.error('저장 실패:', error);
      setSaveMessage('저장에 실패했습니다');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaveLoading(false);
    }
  };

  if (!isOpen) return null;

  // 수정 가능한 컬럼들 필터링
  const editableColumns = columns.filter(col => col.cell_visible && !col.read_only);

  const renderField = (column) => {
    const value = editData[column.column_id] || '';
    const isRequired = column.required || false;

    const commonProps = {
      value,
      onChange: (e) => handleFieldChange(column.column_id, e.target.value),
      disabled: column.read_only
    };

    switch (column.control_type) {
      case "CheckBox":
        return (
          <CheckboxContainer>
            <input
              type="checkbox"
              checked={value === "true" || value === true}
              onChange={(e) => handleFieldChange(column.column_id, e.target.checked)}
              disabled={column.read_only}
            />
            체크
          </CheckboxContainer>
        );

      case "DateBox":
        return (
          <FieldInput
            type="date"
            {...commonProps}
          />
        );

      case "DateTimeBox":
        return (
          <FieldInput
            type="datetime-local"
            {...commonProps}
          />
        );

      case "TextareaBox":
        return (
          <FieldTextarea
            {...commonProps}
            rows="4"
          />
        );

      case "SelectBox":
        // 코드 바인딩 처리
        if (["S", "M", "C"].includes(column.bind_type) && codeDisplayMap[column.bind_key]) {
          const options = codeDisplayMap[column.bind_key];
          return (
            <FieldSelect {...commonProps}>
              <option value="">선택하세요</option>
              {Object.entries(options).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </FieldSelect>
          );
        }
        return <FieldInput type="text" {...commonProps} />;

      default:
        return (
          <FieldInput
            type="text"
            {...commonProps}
          />
        );
    }
  };

  return (
    <>
      <FullScreenOverlay isOpen={isOpen} onClick={onClose} />
      <EditContainer isOpen={isOpen}>
        <EditHeader>
          <EditTitle>편집</EditTitle>
          <HeaderButtons>
            <HeaderButton primary onClick={handleSave} disabled={isLoading}>
              {isLoading ? (
                <FontAwesomeIcon icon={faSpinner} className="spinner" />
              ) : (
                <FontAwesomeIcon icon={faSave} />
              )}
              {isLoading ? '저장중...' : '저장'}
            </HeaderButton>
            <HeaderButton onClick={onClose} disabled={isLoading}>
              <FontAwesomeIcon icon={faTimes} />
            </HeaderButton>
          </HeaderButtons>
        </EditHeader>
        
        <EditContent>
          {editableColumns.map((column) => (
            <FieldGroup key={column.column_id}>
              <FieldLabel>
                {column.mapping_name || column.column_title}
                {column.required && <span className="required">*</span>}
              </FieldLabel>
              {renderField(column)}
            </FieldGroup>
          ))}
        </EditContent>

        <SaveMessage show={!!saveMessage}>
          {saveMessage}
        </SaveMessage>
      </EditContainer>
    </>
  );
};

export default MobileEditForm;